import { ConsentFeatureTypeV1, ConsentFormV1, ConsentTypeV1 } from '@csp/dto';
import { OrgService } from '@csp/csp-common-org';
import { first } from 'lodash/fp';
import { ApiOptions, CspError, Maybe, StateAssert, User } from '@csp/csp-common-model';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { notEmpty } from '@csp/csp-common-util';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ConsentActionOutcomeV1, ConsentArtifactV1, ConsentRequestOutcomeV1 } from '@csp/dmdp-api-consent-dto';
import { PatientContentService } from '@csp/csp-pod-content-service';
import { CmsConsentDocument } from '../model/CmsConsentDocument';
import { ConsentPlatform } from '../model/ConsentPlatform';
import { matchesPlatform } from '../util/consentPlatformUtils';
import { ConsentContentService } from './ConsentContentService';

const getMobileAppEulaAndPrivacyPolicyWarningTexts = async (
  locale: string,
  apiOptions?: ApiOptions,
): Promise<{
  eulaWarningText: string;
  privacyPolicyWarningText: string;
}> => {
  const data = await PatientContentService.getGenericBlock('appPatientConsentScreen', 'consent', locale, apiOptions);

  return { eulaWarningText: data.textLabel.eula.label, privacyPolicyWarningText: data.textLabel.privacyPolicy.label };
};

const isMissingWarningTextAndIsMobileAppClient = (document: CmsConsentDocument, clientType: ConsentPlatform): boolean =>
  (!document.declineAlertWarningText || document.declineAlertWarningText.length === 0) &&
  clientType === ConsentPlatform.APP;

const toOutcomeWithArtifactAndDocument = async (
  outcome: ConsentRequestOutcomeV1,
  countryCode: string,
  locale: string,
  clientType: ConsentPlatform,
  warningTexts: { eulaWarningText: string; privacyPolicyWarningText: string } | undefined,
  apiOptions: ApiOptions | undefined,
): Promise<Maybe<OutcomeWithArtifactAndDocument>> => {
  const artifact = await ConsentRestServiceV1.getArtifactV1(toRestOptions(apiOptions), outcome.artifactCode);
  if (artifact && countryCode === artifact.meta?.country) {
    const version = artifact.documents[0]?.version || '1.0';

    const document = await ConsentContentService.getConsentDocument({
      config: {
        cmsConsentType: outcome.artifactType,
        clientType,
        country: countryCode,
        version,
      },
      locale,
      apiOptions,
    });
    if (isMissingWarningTextAndIsMobileAppClient(document, clientType)) {
      const consentType = outcome.artifactType as ConsentType;
      if (consentType === ConsentType.EULA) {
        document.declineAlertWarningText = warningTexts?.eulaWarningText || '';
      } else if (consentType === ConsentType.PRIVACY_POLICY) {
        document.declineAlertWarningText = warningTexts?.privacyPolicyWarningText || '';
      }
    }
    return { outcome, artifact, document, version };
  } else {
    return undefined;
  }
};

const hasMatchingClientTypeAndConsentTypeAndMissingApprovals = (
  outcome: ConsentRequestOutcomeV1,
  clientType: ConsentPlatform,
  consentTypes: ConsentType[],
): boolean =>
  matchesPlatform(outcome.artifactTags || [], clientType) &&
  consentTypes.some(ct => ct === outcome.artifactType) &&
  (!outcome.approvalIds || outcome.approvalIds.length === 0);

const hasMatchingClientTypeAndConsentTypeAndApprovals = (
  outcome: ConsentRequestOutcomeV1,
  clientType: ConsentPlatform,
  consentTypes: ConsentType[],
): boolean =>
  matchesPlatform(outcome.artifactTags || [], clientType) &&
  consentTypes.some(ct => ct === outcome.artifactType) &&
  outcome.approvalIds !== undefined &&
  outcome.approvalIds.length > 0;

const hasMatchingClientTypeAndConsentType = (
  outcome: ConsentActionOutcomeV1,
  clientType: ConsentPlatform,
  consentTypes: ConsentType[],
): boolean =>
  matchesPlatform(outcome.artifactTags || [], clientType) && consentTypes.some(ct => ct === outcome.artifactType);

type OutcomeWithArtifactAndDocument = {
  artifact: ConsentArtifactV1;
  document: CmsConsentDocument;
  outcome: ConsentRequestOutcomeV1;
  version: string;
};

const toConsentForm = ({ artifact, document }: OutcomeWithArtifactAndDocument): ConsentFormV1 => {
  const documentReference = artifact.documents[0]?.reference;
  StateAssert.isDefined(documentReference, 'Unexpected undefined documentReference');

  return {
    id: `Legacy-${artifact.type}-${artifact.artifactCode}-${artifact.documents[0]?.version || ''}`,
    artifact: { artifactCode: artifact.artifactCode, documentReference },
    config: { mandatory: true, onFeature: [{ type: ConsentFeatureTypeV1.ON_ONBOARDING }], enableReconsent: true },
    consentType: ConsentTypeV1.CONSENT_FORM_V1,
    document: {
      header: document.header,
      subHeader: document.subHeader,
      bodyMd: document.bodyMd,
      declineAlertWarningText: document.declineAlertWarningText || '',
      readMoreButtonText: document.readMoreButtonText || '',
      readMoreButtonTextAccessibilityLabel: document.readMoreButtonTextAccessibilityLabel || '',
      readMoreButtonTextAccessibilityHint: document.readMoreButtonTextAccessibilityHint || '',
      acceptDialogHeader: '',
      declineConfirmationDialogHeader: '',
      withdrawConfirmationDialogHeader: '',
      withdrawDataProcessingOptionsDialogBody: '',
      withdrawDataProcessingOptionsDialogRadioLabel: '',
      withdrawDataProcessingOptionsDialogContinueUsingData: {
        accessibilityHint: '',
        accessibilityLabel: '',
        label: '',
      },
      withdrawDataProcessingOptionsDialogStopUsingData: {
        accessibilityHint: '',
        accessibilityLabel: '',
        label: '',
      },
    },
  };
};

const getPendingLegacyConsents = async (
  user: User,
  clientType: ConsentPlatform,
  consentTypes: ConsentType[],
  apiOptions?: ApiOptions,
): Promise<ConsentFormV1[]> => {
  const outcomes = await ConsentRestServiceV1.getRequestOutcomesV1(toRestOptions(apiOptions));
  const countryCode = await getCountryCode(user, apiOptions);

  const matchingOutcomes = outcomes.consentRequestOutcomes
    .filter(outcome => hasMatchingClientTypeAndConsentTypeAndMissingApprovals(outcome, clientType, consentTypes))
    .sort(({ artifactType }) => (artifactType === 'EULA' ? -1 : 1));

  if (matchingOutcomes.length === 0) {
    return [];
  }

  const outcomesWithArtifactAndDocument = await getMappedOutcomesWithArtifacts(
    clientType,
    user.localization.locale,
    countryCode,
    matchingOutcomes,
    apiOptions,
  );
  return outcomesWithArtifactAndDocument.map(toConsentForm);
};

const getApprovedLegacyConsents = async (
  user: User,
  clientType: ConsentPlatform,
  consentTypes: ConsentType[],
  apiOptions?: ApiOptions,
): Promise<ConsentFormV1[]> => {
  const outcomes = await ConsentRestServiceV1.getRequestOutcomesV1(toRestOptions(apiOptions));
  const countryCode = await getCountryCode(user, apiOptions);

  const matchingOutcomes = outcomes.consentRequestOutcomes
    .filter(outcome => hasMatchingClientTypeAndConsentTypeAndApprovals(outcome, clientType, consentTypes))
    .sort(({ artifactType }) => (artifactType === 'EULA' ? -1 : 1));

  if (matchingOutcomes.length === 0) {
    return [];
  }

  const outcomesWithArtifactAndDocument = await getMappedOutcomesWithArtifacts(
    clientType,
    user.localization.locale,
    countryCode,
    matchingOutcomes,
    apiOptions,
  );
  return outcomesWithArtifactAndDocument.map(toConsentForm);
};

const getCountryCode = async (user: User, apiOptions?: ApiOptions): Promise<string> => {
  const countries = await OrgService.fetchCountriesByIds(user.orgIdsIncludingParents, apiOptions);

  const countryCode = first(countries)?.countryCode?.toUpperCase();

  if (!countryCode) {
    throw CspError.badState('Found no country org nodes');
  }
  return countryCode;
};

const getMappedOutcomesWithArtifacts = async (
  clientType: ConsentPlatform,
  locale: string,
  countryCode: string,
  matchingOutcomes: ConsentRequestOutcomeV1[],
  apiOptions?: ApiOptions,
): Promise<Array<OutcomeWithArtifactAndDocument>> => {
  const warningTexts =
    clientType === ConsentPlatform.APP
      ? await getMobileAppEulaAndPrivacyPolicyWarningTexts(locale, apiOptions)
      : undefined;
  const maybeOutcomesWithArtifacts = await Promise.all(
    matchingOutcomes.map(outcome =>
      toOutcomeWithArtifactAndDocument(outcome, countryCode, locale, clientType, warningTexts, apiOptions),
    ),
  );

  return maybeOutcomesWithArtifacts.filter(notEmpty);
};

export const LegacyConsentService = {
  getPendingLegacyConsents,
  getApprovedLegacyConsents,
  hasMatchingClientTypeAndConsentType,
};
